import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { formatMinutes } from './format';
import styles from './Workload.module.css';
import type { DayLoad } from './model';

/** A full bar = this many occupied hours (a working day). */
const FULL_BAR_MINUTES = 8 * 60;

/**
 * What one day holds, small enough to sit beside a date: the occupied time of its timed events
 * (a thin bar and the hours; a phone gets only the hours) or, when nothing is timed, just the
 * number of events. Screen readers get one full sentence.
 */
export function Workload({
  load,
  compact = false,
}: {
  load: DayLoad | undefined;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  if (!load || load.count === 0) return null;
  const events = t('calendar.workload.events', { count: load.count });
  const timed = load.minutes > 0;
  const label = timed
    ? t('calendar.workload.label', {
        events,
        time: t('calendar.workload.occupied', { time: formatMinutes(load.minutes, t) }),
      })
    : events;
  const short = timed
    ? compact
      ? t('calendar.workload.h', { h: Math.round((load.minutes / 60) * 10) / 10 })
      : formatMinutes(load.minutes, t)
    : String(load.count);
  return (
    <span
      className={styles.load}
      data-testid="day-workload"
      data-minutes={load.minutes}
      data-count={load.count}
      title={label}
    >
      {timed && !compact && (
        <span
          className={styles.bar}
          style={
            {
              '--fill': `${String(Math.min(100, (load.minutes / FULL_BAR_MINUTES) * 100))}%`,
            } as CSSProperties
          }
          aria-hidden
        />
      )}
      <span aria-hidden>{short}</span>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}
